import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Blob as BlobType } from '@/services/types/blob-types';
import { CollectionNode } from '@/services/types/collection-types';

import {
  checkFolderSelectionState,
  computeNodeMaps,
  expandMatchingPaths,
  getDescendantBlobs,
  NodeMaps,
} from '../utils/treeUtils';
import { useDocumentExplorerState } from './DocumentExplorerStateContext';

// Define the combined context type
type DocumentTreeContextType = {
  // TreeView state
  expandedFolders: Set<string>;
  activeNodeId: string;
  toggleFolder: (id: string) => void;
  setActiveNodeId: (id: string) => void;
  setActiveNodeIdAndClearFiles: (id: string) => void;
  setActiveFileAndSelect: (fileId: string) => void;
  setExpandedFolders: (folderIds: Set<string>) => void;
  expandFolders: (folderIds: string[]) => void;
  expandAllFolders: () => void;
  collapseAllFolders: () => void;

  // FileSelection state
  selectedFileIds: Set<string>;
  toggleFileSelection: (fileId: string) => void;
  selectAllFiles: (fileIds: string[]) => void;
  clearSelection: () => void;
  clearSelectionAndSelectFile: (fileId: string) => void;
  isFileSelected: (fileId: string) => boolean;
  selectedCount: number;
  toggleSelectAll: (files: BlobType[] | undefined) => void;
  isAllSelected: (files: BlobType[] | undefined) => boolean;
  selectAllInFolder: (
    folderId: string,
    blobs: BlobType[] | undefined,
    node?: CollectionNode,
  ) => void;
  isFolderFullySelected: (
    folderId: string,
    blobs: BlobType[] | undefined,
    node?: CollectionNode,
  ) => boolean;

  // Folder selection state
  selectedFolderIds: Set<string>;
  toggleFolderSelection: (folderId: string) => void;
  clearFolderSelection: () => void;
  selectedFolderCount: number;

  // Shared state
  nodeMaps: NodeMaps;
  isTextInputActive: boolean;
  beginTextInputInteraction: () => void;
  endTextInputInteraction: () => void;
};

// Create the context with default values
const DocumentTreeContext = createContext<DocumentTreeContextType | undefined>(
  undefined,
);

interface DocumentTreeProviderProps {
  children: React.ReactNode;
  rootNodeId?: string;
  collectionTree?: CollectionNode | null;
}

export const DocumentTreeProvider = ({
  children,
  rootNodeId,
  collectionTree,
}: DocumentTreeProviderProps) => {
  // Get persisted state from parent context (if available)
  const persistedState = useDocumentExplorerState();

  // State initialization - use persisted state if available
  const [expandedFolders, setExpandedFoldersInternal] = useState<Set<string>>(
    () => {
      if (
        persistedState?.hasInitialized &&
        persistedState.state.expandedFolders.size > 0
      ) {
        return persistedState.state.expandedFolders;
      }
      return new Set(rootNodeId ? [rootNodeId] : []);
    },
  );
  const [activeNodeId, setActiveNodeIdInternal] = useState<string>(() => {
    if (persistedState?.hasInitialized && persistedState.state.activeNodeId) {
      return persistedState.state.activeNodeId;
    }
    return '';
  });
  const [selectedFileIds, setSelectedFileIdsInternal] = useState<Set<string>>(
    () => {
      if (
        persistedState?.hasInitialized &&
        persistedState.state.selectedFileIds.size > 0
      ) {
        return persistedState.state.selectedFileIds;
      }
      return new Set();
    },
  );
  const [selectedFolderIds, setSelectedFolderIdsInternal] = useState<
    Set<string>
  >(() => {
    if (
      persistedState?.hasInitialized &&
      persistedState.state.selectedFolderIds.size > 0
    ) {
      return persistedState.state.selectedFolderIds;
    }
    return new Set();
  });
  const [textInputInteractionCount, setTextInputInteractionCount] = useState(0);
  const expandedFoldersRef = useRef<Set<string>>(expandedFolders);
  const hasInitializedRef = useRef(persistedState?.hasInitialized ?? false);

  // Wrapper functions for state updates
  const setExpandedFolders = useCallback(
    (folders: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setExpandedFoldersInternal((prev) => {
        const newFolders =
          typeof folders === 'function' ? folders(prev) : folders;
        return newFolders;
      });
    },
    [],
  );

  const setActiveNodeId = useCallback((id: string) => {
    setActiveNodeIdInternal(id);
  }, []);

  const setSelectedFileIds = useCallback(
    (ids: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setSelectedFileIdsInternal((prev) => {
        const newIds = typeof ids === 'function' ? ids(prev) : ids;
        return newIds;
      });
    },
    [],
  );

  const setSelectedFolderIds = useCallback(
    (ids: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setSelectedFolderIdsInternal((prev) => {
        const newIds = typeof ids === 'function' ? ids(prev) : ids;
        return newIds;
      });
    },
    [],
  );

  // Sync state changes to persisted context via useEffect (avoids setState during render)
  useEffect(() => {
    persistedState?.setExpandedFolders(expandedFolders);
  }, [expandedFolders, persistedState]);

  useEffect(() => {
    persistedState?.setActiveNodeId(activeNodeId);
  }, [activeNodeId, persistedState]);

  useEffect(() => {
    persistedState?.setSelectedFileIds(selectedFileIds);
  }, [selectedFileIds, persistedState]);

  useEffect(() => {
    persistedState?.setSelectedFolderIds(selectedFolderIds);
  }, [selectedFolderIds, persistedState]);

  // Create node maps for efficient tree operations
  const nodeMaps = useMemo(
    () =>
      collectionTree
        ? computeNodeMaps(collectionTree)
        : {
            idToNodeMap: new Map(),
            pathToNodeMap: new Map(),
            nodeToPathMap: new Map(),
          },
    [collectionTree],
  );

  // Update refs and initial state
  useEffect(() => {
    expandedFoldersRef.current = expandedFolders;
  }, [expandedFolders]);

  useEffect(() => {
    if (rootNodeId && !activeNodeId) {
      setActiveNodeId(rootNodeId);
    }
  }, [rootNodeId, activeNodeId, setActiveNodeId]);

  // Collapse all folders by default on initial load only (not on refetches)
  // Skip if we've already initialized (either from persisted state or previous load)
  useEffect(() => {
    if (
      nodeMaps.idToNodeMap.size > 0 &&
      rootNodeId &&
      !hasInitializedRef.current
    ) {
      setExpandedFolders(new Set([rootNodeId]));
      hasInitializedRef.current = true;
    }
  }, [nodeMaps, rootNodeId, setExpandedFolders]);

  // Mark persisted state as initialized after first initialization
  useEffect(() => {
    if (
      hasInitializedRef.current &&
      persistedState &&
      !persistedState.hasInitialized
    ) {
      persistedState.setHasInitialized(true);
    }
  }, [persistedState]);

  // TreeView operations
  const toggleFolder = useCallback(
    (folderId: string) => {
      setExpandedFolders((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(folderId)) {
          newSet.delete(folderId);
        } else {
          newSet.add(folderId);
        }
        return newSet;
      });
    },
    [setExpandedFolders],
  );

  const expandFolders = useCallback(
    (folderIds: string[]) => {
      setExpandedFolders((prev) => {
        const newSet = new Set(prev);
        folderIds.forEach((id) => newSet.add(id));
        return newSet;
      });
    },
    [setExpandedFolders],
  );

  // Expand all folders using nodeMaps
  const expandAllFolders = useCallback(() => {
    const allFolderIds = Array.from(nodeMaps.idToNodeMap.keys());
    setExpandedFolders(new Set(allFolderIds));
  }, [nodeMaps, setExpandedFolders]);

  // Collapse all folders except root
  const collapseAllFolders = useCallback(() => {
    setExpandedFolders(new Set(rootNodeId ? [rootNodeId] : []));
  }, [rootNodeId, setExpandedFolders]);

  // Function for general node selection that clears file selection
  const setActiveNodeIdAndClearFiles = useCallback(
    (nodeId: string) => {
      setActiveNodeId(nodeId);
      setSelectedFileIds(new Set()); // Clear file selection
    },
    [setActiveNodeId, setSelectedFileIds],
  );

  // New function specifically for file nodes that also selects the file
  const setActiveFileAndSelect = useCallback(
    (fileId: string) => {
      setActiveNodeId(fileId);
      setSelectedFileIds(new Set([fileId])); // Clear other selections and select this file
    },
    [setActiveNodeId, setSelectedFileIds],
  );

  // File selection operations
  const toggleFileSelection = useCallback(
    (fileId: string) => {
      setSelectedFileIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(fileId)) {
          newSet.delete(fileId);
        } else {
          newSet.add(fileId);
        }
        return newSet;
      });
    },
    [setSelectedFileIds],
  );

  const selectAllFiles = useCallback(
    (fileIds: string[]) => {
      setSelectedFileIds(new Set(fileIds));
    },
    [setSelectedFileIds],
  );

  const clearSelection = useCallback(() => {
    setSelectedFileIds(new Set());
  }, [setSelectedFileIds]);

  const clearSelectionAndSelectFile = useCallback(
    (fileId: string) => {
      setSelectedFileIds(new Set([fileId]));
    },
    [setSelectedFileIds],
  );

  const isFileSelected = useCallback(
    (fileId: string) => selectedFileIds.has(fileId),
    [selectedFileIds],
  );

  const isAllSelected = useCallback(
    (files?: BlobType[]) =>
      files?.length
        ? files.every((file) => selectedFileIds.has(file.id))
        : false,
    [selectedFileIds],
  );

  const toggleSelectAll = useCallback(
    (files?: BlobType[]) => {
      if (!files?.length) return;
      setSelectedFileIds(
        isAllSelected(files)
          ? new Set()
          : new Set(files.map((file) => file.id)),
      );
    },
    [isAllSelected, setSelectedFileIds],
  );

  const isFolderFullySelected = useCallback(
    (folderId: string, blobs?: BlobType[], node?: CollectionNode) =>
      blobs?.length
        ? checkFolderSelectionState(folderId, blobs, selectedFileIds, nodeMaps)
        : false,
    [selectedFileIds, nodeMaps],
  );

  const selectAllInFolder = useCallback(
    (folderId: string, blobs?: BlobType[], node?: CollectionNode) => {
      if (!blobs?.length) return;

      const folderBlobs = getDescendantBlobs(folderId, blobs, nodeMaps);
      const isFullySelected = isFolderFullySelected(folderId, blobs, node);

      setSelectedFileIds((prev) => {
        const newSelection = new Set(prev);
        folderBlobs.forEach((blob) => {
          if (isFullySelected) {
            newSelection.delete(blob.id);
          } else {
            newSelection.add(blob.id);
          }
        });
        return newSelection;
      });
    },
    [isFolderFullySelected, nodeMaps, setSelectedFileIds],
  );

  const toggleFolderSelection = useCallback(
    (folderId: string) => {
      setSelectedFolderIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(folderId)) {
          newSet.delete(folderId);
        } else {
          newSet.add(folderId);
        }
        return newSet;
      });
    },
    [setSelectedFolderIds],
  );

  const clearFolderSelection = useCallback(() => {
    setSelectedFolderIds(new Set());
  }, [setSelectedFolderIds]);

  const beginTextInputInteraction = useCallback(() => {
    setTextInputInteractionCount((prev) => prev + 1);
  }, []);

  const endTextInputInteraction = useCallback(() => {
    setTextInputInteractionCount((prev) => (prev > 0 ? prev - 1 : 0));
  }, []);

  const isTextInputActive = textInputInteractionCount > 0;

  // Create memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      // TreeView state
      expandedFolders,
      activeNodeId,
      toggleFolder,
      setActiveNodeId,
      setActiveNodeIdAndClearFiles,
      setActiveFileAndSelect,
      setExpandedFolders,
      expandFolders,
      expandAllFolders,
      collapseAllFolders,

      // FileSelection state
      selectedFileIds,
      toggleFileSelection,
      selectAllFiles,
      clearSelection,
      clearSelectionAndSelectFile,
      isFileSelected,
      selectedCount: selectedFileIds.size,
      toggleSelectAll,
      isAllSelected,
      selectAllInFolder,
      isFolderFullySelected,

      // Folder selection state
      selectedFolderIds,
      toggleFolderSelection,
      clearFolderSelection,
      selectedFolderCount: selectedFolderIds.size,

      // Shared state
      nodeMaps,
      isTextInputActive,
      beginTextInputInteraction,
      endTextInputInteraction,
    }),
    [
      expandedFolders,
      activeNodeId,
      toggleFolder,
      setActiveNodeId,
      setActiveNodeIdAndClearFiles,
      setActiveFileAndSelect,
      setExpandedFolders,
      expandFolders,
      expandAllFolders,
      collapseAllFolders,
      selectedFileIds,
      toggleFileSelection,
      selectAllFiles,
      clearSelection,
      clearSelectionAndSelectFile,
      isFileSelected,
      toggleSelectAll,
      isAllSelected,
      selectAllInFolder,
      isFolderFullySelected,
      selectedFolderIds,
      toggleFolderSelection,
      clearFolderSelection,
      nodeMaps,
      isTextInputActive,
      beginTextInputInteraction,
      endTextInputInteraction,
    ],
  );

  return (
    <DocumentTreeContext.Provider value={contextValue}>
      {children}
    </DocumentTreeContext.Provider>
  );
};

// Custom hook for using the context
export const useDocumentTree = (): DocumentTreeContextType => {
  const context = useContext(DocumentTreeContext);
  if (!context) {
    throw new Error(
      'useDocumentTree must be used within a DocumentTreeProvider',
    );
  }
  return context;
};

type DocumentTreeInputLock = {
  isTextInputActive: boolean;
  beginTextInputInteraction: () => void;
  endTextInputInteraction: () => void;
};

export const useDocumentTreeInputLock = (): DocumentTreeInputLock => {
  const context = useContext(DocumentTreeContext);

  if (!context) {
    return {
      isTextInputActive: false,
      beginTextInputInteraction: () => {},
      endTextInputInteraction: () => {},
    };
  }

  return {
    isTextInputActive: context.isTextInputActive,
    beginTextInputInteraction: context.beginTextInputInteraction,
    endTextInputInteraction: context.endTextInputInteraction,
  };
};

// Utility function to expand tree paths for search results
export const expandPathsForSearch = (
  collectionTree: CollectionNode,
  matchingBlobs: BlobType[],
): Set<string> => {
  if (!collectionTree || !matchingBlobs?.length) {
    return new Set([collectionTree.id]);
  }

  const nodeMaps = computeNodeMaps(collectionTree);
  return expandMatchingPaths(matchingBlobs, collectionTree.id, nodeMaps);
};
